//
// types.ts — the shared vocabulary
//
// Direct translation of the iOS app's `Models/Models.swift`, which is
// itself a 1:1 port of the original Fuser app's `src/types.ts`. This file
// closes that loop: TypeScript -> Swift -> TypeScript, with the live-data
// fields the Swift side gained on 2026-08-21 carried across.
//
// The rule for this whole web app, borrowed from akaspace-web's CLAUDE.md:
// **the phone is the spec.** If the iOS app doesn't do it, the web doesn't
// do it. When these two disagree, the Swift is right and this is a bug.
//

/** Every kind of thing the map can plot. Mirrors `ObjectCategory`. */
export type ObjectCategory =
  | "starlink"
  | "active"
  | "debris"
  | "rocket_body"
  | "neo_hazardous"
  | "neo_safe"
  | "flight"
  | "vessel"
  | "subsolar"
  | "sublunar"
  | "military_aircraft"
  | "place";

/**
 * A CelesTrak GP element set. Mirrors `OrbitalElements` in Swift — the six
 * Keplerian elements plus the epoch they were issued at. This is what makes
 * a real ground track possible instead of an invented one.
 */
export interface OrbitalElements {
  /** Revolutions per day. */
  meanMotion: number;
  eccentricity: number;
  /** Degrees. */
  inclination: number;
  /** Right ascension of the ascending node, degrees. */
  raan: number;
  /** Argument of perigee, degrees. */
  argOfPericenter: number;
  /** Mean anomaly at epoch, degrees. */
  meanAnomaly: number;
  /** Epoch as milliseconds since the Unix epoch. */
  epochMs: number;
  noradCatId: number;
  objectId?: string;
  bstar?: number;
}

/**
 * Anything plottable on the map — a satellite, an aircraft, a ship, a
 * pinned place, the sub-solar point — all one shape. Almost every field is
 * optional for the same reason it is in Swift: an aircraft has no orbital
 * period and a satellite has no tail number.
 */
export interface TrackedObject {
  id: string;
  noradId?: number;
  mmsi?: string;
  icao24?: string;
  name: string;
  category: ObjectCategory;
  typeLabel: string;
  latitude: number;
  longitude: number;

  altitudeKm?: number;
  altitudeFt?: number;
  velocityKms?: number;
  speedKnots?: number;
  headingDeg?: number;

  origin?: string;
  destination?: string;
  vesselType?: string;
  airline?: string;
  flightNumber?: string;

  orbitalPeriodMin?: number;
  inclinationDeg?: number;

  dataSource: string;
  lastUpdated: string;

  diameterMeters?: number;
  missDistanceKm?: number;

  // Live ADS-B
  registration?: string;
  aircraftTypeCode?: string;
  squawk?: string;
  verticalRateFpm?: number;
  isOnGround?: boolean;
  positionAgeSeconds?: number;

  // Planespotters photo (attribution is mandatory — see api/photos.ts)
  photoUrl?: string;
  photoPageUrl?: string;
  photoCredit?: string;

  // Real orbit
  elements?: OrbitalElements;

  /**
   * Which second-tier button owns this contact — set by the feed that
   * fetched it, because only the feed knows. Aircraft get theirs from the
   * ADS-B emitter category, ships from their AIS type band, satellites from
   * the CelesTrak group they arrived in.
   *
   * The renderer reads this to decide visibility, so a contact with no
   * `layer` is drawn by nothing and is a bug in whichever feed produced it.
   */
  layer?: ContactKey;
}

/**
 * The four genres on the arc menu's first tier, plus the settings button
 * that is not a genre. Build 1.5, item 7.
 */
export type GenreId = "air" | "sea" | "sat" | "wet";

/**
 * One key per second-tier button — four per genre, sixteen in all. These
 * are what actually toggle; a genre button only opens its tier.
 *
 * SEA's four are the AIS ship-type bands (cargo 70-79, tanker 80-89,
 * passenger 60-69, fishing and working 30-59), so they are real divisions
 * rather than invented ones. **There is deliberately no military boats
 * key** — warships switch their AIS transponders off, so the layer would
 * be permanently empty or permanently invented. See
 * `docs/builds/1.5/spec.md` #7.
 */
export type ContactKey =
  | "airCommercial" | "airPrivate" | "airMilitary" | "airRotorcraft"
  | "seaCargo" | "seaTanker" | "seaPassenger" | "seaFishing"
  | "satStarlink" | "satStations" | "satNavWeather" | "satLuminaries"
  | "wetWind" | "wetPrecipitation" | "wetTemperature" | "wetClouds";

/**
 * Things that are not contacts and do not belong on a genre button — the
 * map's own furniture. These live in Settings -> Map Furniture and are the
 * only layers on at a cold open. Build 1.5, item 12.
 */
export type FurnitureKey = "graticule" | "parallels" | "places" | "orbitalTrails";

export type LayerKey = ContactKey | FurnitureKey;

export const CONTACT_KEYS: ContactKey[] = [
  "airCommercial", "airPrivate", "airMilitary", "airRotorcraft",
  "seaCargo", "seaTanker", "seaPassenger", "seaFishing",
  "satStarlink", "satStations", "satNavWeather", "satLuminaries",
  "wetWind", "wetPrecipitation", "wetTemperature", "wetClouds",
];

export const FURNITURE_KEYS: FurnitureKey[] = [
  "graticule", "parallels", "places", "orbitalTrails",
];

/**
 * Mirrors `LayerState` in `Models.swift`.
 *
 * **Restructured in build 1.5, item 7.** It used to be twenty-odd named
 * booleans with a matching named opacity each, and every consumer — the
 * dial, the renderer, the settings sheet — carried its own switch statement
 * over them. Two maps keyed by `LayerKey` instead, so the arc menu can
 * toggle whatever its catalogue points at without knowing what it is, and
 * there is exactly one list of what exists.
 */
export interface LayerState {
  on: Record<LayerKey, boolean>;
  opacity: Record<LayerKey, number>;
}

/**
 * What is showing when the app opens cold.
 *
 * **Every contact layer starts off** — build 1.5, item 1. Dave: *"Per the
 * design things are tracked live, toggled on\\off, and should all be of by
 * default."* Before this, all of them shipped on, so the instrument opened
 * with every feed drawing at once and no way to tell which was which.
 *
 * The four that stay **on** are map furniture, not contacts. With those off
 * too the app would open as a featureless black disc, which is not "off by
 * default", it is broken. Orbital trails draw nothing at all while no
 * satellites are showing, so leaving them on costs nothing.
 *
 * Must stay identical to `LayerState` in `Models.swift`.
 */
export function defaultLayerState(): LayerState {
  const on = {} as Record<LayerKey, boolean>;
  const opacity = {} as Record<LayerKey, number>;

  for (const k of CONTACT_KEYS) {
    on[k] = false;
    opacity[k] = 100;
  }
  for (const k of FURNITURE_KEYS) {
    on[k] = true;
    opacity[k] = 100;
  }

  // The weather washes were never drawn at full strength — they are a
  // backdrop, not a contact, and at 100 they bury the map under them.
  // These four numbers are carried over unchanged from the layer state
  // this replaced.
  opacity.wetClouds = 60;
  opacity.wetPrecipitation = 70;
  opacity.wetWind = 60;
  opacity.wetTemperature = 50;

  return { on, opacity };
}

/** Mirrors `WeatherParticle` — the wind-vector particle system. */
export interface WeatherParticle {
  lat: number;
  lon: number;
  age: number;
  maxAge: number;
  speed: number;
}

/** A geographic point from Open-Meteo's geocoder. Mirrors `PlacesService.Place`. */
export interface Place {
  id: number;
  name: string;
  country?: string;
  admin1?: string;
  latitude: number;
  longitude: number;
  elevationM?: number;
  population?: number;
  timezone?: string;
  featureCode?: string;
}

export function qualifiedPlaceName(p: Place): string {
  return [p.name, p.admin1, p.country].filter(Boolean).join(", ");
}

/**
 * The marker colours, in one place. These are the exact RGB values the
 * Swift views use, converted from SwiftUI's 0-1 components to CSS. Keeping
 * them here rather than in the renderer means the map, the legend and the
 * dial cannot drift apart the way they would if each restated them.
 */
export const CATEGORY_COLORS: Record<ObjectCategory, string> = {
  starlink: "rgb(59, 130, 245)",
  active: "rgb(168, 84, 247)",
  debris: "rgb(148, 163, 184)",
  rocket_body: "rgb(148, 163, 184)",
  neo_hazardous: "rgb(240, 69, 69)",
  neo_safe: "rgb(250, 145, 61)",
  flight: "rgb(56, 189, 247)",
  vessel: "rgb(15, 184, 130)",
  subsolar: "rgb(255, 199, 51)",
  sublunar: "rgb(204, 212, 222)",
  military_aircraft: "rgb(250, 115, 89)",
  place: "rgb(153, 242, 191)",
};

/** The instrument's accent — cyan, used by every piece of chrome. */
export const ACCENT = "rgb(33, 212, 237)";
